import { TeacherRegisterForm } from '../../components/RegisterForm/TeacherForm/index';
import { useEffect, useState } from 'react';
import { StudentRegisterForm } from '../../components/RegisterForm/StudentForm/index';
import { ResponsibleRegisterForm } from '../../components/RegisterForm/ResponsibleForm/index';
import { Divider, IconButton } from '@mui/material';
import clsx from 'clsx';
import LogoPng from '../../assets/Logo.png';
import StudentActiveIcon from '../../assets/icons/StudentActiveIcon.svg';
import StudentIcon from '../../assets/icons/StudentIcon.svg';
import TeacherActiveIcon from '../../assets/icons/TeacherActiveIcon.svg';
import TeacherIcon from '../../assets/icons/TeacherIcon.svg';
import ResponsibleActiveIcon from '../../assets/icons/ResponsibleActiveIcon.svg';
import ResponsibleIcon from '../../assets/icons/ResponsibleIcon.svg';
import EmployeeIcon from '../../assets/icons/EmployeeIcon.svg';
import EmployeeActiveIcon from '../../assets/icons/EmployeeActiveIcon.svg';
import AdminIcon from '../../assets/icons/AdminIcon.svg';
import AdminActiveIcon from '../../assets/icons/AdminActiveIcon.svg';
import { EmployeeRegisterForm } from '../../components/RegisterForm/EmployeeForm';
import { AdminRegisterForm } from '../../components/RegisterForm/AdminForm';
import { Button } from '../../components/Button';
import { ArrowBack } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { UserDetailedRes, UserRole } from '../../services/api.types';
import { useAppSelector } from '../../hooks';
import { ROUTES } from '../../utils/constants';
import { translateUserRoles } from '../../utils/translate-enums';
import { UsersFormProps } from './form-props.types';
import { usersService } from '../../services/users.service';

type FormType = UserRole;

type UserTypeIconProps = {
  onClick?: () => void;
  iconSrc: string;
  label: string;
  active?: boolean;
};

const UserTypeIcon = ({
  active,
  iconSrc,
  label,
  onClick,
}: UserTypeIconProps) => {
  return (
    <div className="flex flex-col items-center gap-1">
      <Button
        className={clsx(
          active ? 'bg-blue-primary' : 'bg-gray-e9',
          'w-20 h-20 xs:w-28 xs:h-28'
        )}
        endIcon={
          <img
            src={iconSrc}
            alt={label}
            className="mr-3 xs:w-10 xs:h-10 w-7 h-7"
          />
        }
        onClick={onClick}
        circular
      />

      <label className="text-blue-primary text-15">{label}</label>
    </div>
  );
};

export type RegisterFormMode = 'CREATE' | 'UPDATE';

export function RegisterPage() {
  const [formMode, setFormMode] = useState<RegisterFormMode>('UPDATE');
  const { id } = useParams();
  const getUser = async (id?: number) =>
    id
      ? await usersService.getUserDetailed(id)
      : await usersService.getMeDetailed();

  const location = useLocation();

  const user = useAppSelector((state) => state.user);

  const [userToUpdate, setUserToUpdate] = useState<
    UserDetailedRes | undefined
  >();
  const [formType, setFormType] = useState<FormType>(
    userToUpdate?.role ?? user?.role ?? UserRole.STUDENT
  );

  // Set the formType and FormMode based on the route path
  useEffect(() => {
    if (location.pathname === ROUTES.users.create) {
      setUserToUpdate(undefined);
      setFormMode('CREATE');
      setFormType(UserRole.STUDENT);
    } else if (location.pathname === ROUTES.users.edit(id ?? '')) {
      setFormMode('UPDATE');
      getUser(Number(id)).then((user) => {
        setUserToUpdate(user);
        setFormType(user.role);
      });
    } else if (location.pathname === ROUTES.users.me) {
      getUser().then((user) => {
        setFormMode('UPDATE');
        setFormType(user.role);
        setUserToUpdate(user);
      });
    }
  }, [location.pathname, id, user?.id]);

  const navigate = useNavigate();

  if (!user) return null;

  const registerForm: Record<
    FormType,
    (usersFormProps: UsersFormProps) => JSX.Element
  > = {
    STUDENT: StudentRegisterForm,
    TEACHER: TeacherRegisterForm,
    RESPONSIBLE: ResponsibleRegisterForm,
    EMPLOYEE: EmployeeRegisterForm,
    ADMIN: AdminRegisterForm,
  };

  const RegisterForm = registerForm[formType];

  const handleIconSrc = (formType: FormType) => {
    switch (formType) {
      case 'STUDENT':
        return StudentActiveIcon;
      case 'TEACHER':
        return TeacherActiveIcon;
      case 'RESPONSIBLE':
        return ResponsibleActiveIcon;
      case 'EMPLOYEE':
        return EmployeeActiveIcon;
      case 'ADMIN':
        return AdminActiveIcon;
      default:
        return StudentActiveIcon;
    }
  };

  const goBack = () => navigate(-1);

  const renderCreate = () => {
    return (
      <div className="w-auto h-screen flex justify-center p-5">
        <div className="hidden lg:block">
          <Button
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              boxShadow: 'none',
            }}
            onClick={goBack}
          >
            <img
              src={LogoPng}
              alt="Logo Plus"
              height={300}
              className={clsx('cursor-pointer')}
            />
          </Button>
        </div>

        <div
          className={clsx(
            'w-full max-w-2xl bg-white',
            'flex flex-col gap-1',
            'rounded-3xl shadow-2xl'
          )}
        >
          <div className="w-full flex flex-col items-center gap-4 p-6 relative">
            <IconButton
              color="secondary"
              className="absolute left-6 top-6"
              onClick={goBack}
              tabIndex={0}
            >
              <ArrowBack />
            </IconButton>
            <span className="text-lg font-bold">O usuário é:</span>

            <div className="flex flex-wrap gap-3 justify-center w-full">
              <UserTypeIcon
                active={formType === 'STUDENT'}
                iconSrc={
                  formType === 'STUDENT' ? StudentActiveIcon : StudentIcon
                }
                label="Estudante"
                onClick={() => setFormType(UserRole.STUDENT)}
              />

              <UserTypeIcon
                active={formType === 'RESPONSIBLE'}
                iconSrc={
                  formType === 'RESPONSIBLE'
                    ? ResponsibleActiveIcon
                    : ResponsibleIcon
                }
                label="Responsável"
                onClick={() => setFormType(UserRole.RESPONSIBLE)}
              />

              {user.role === UserRole.ADMIN && (
                <>
                  <UserTypeIcon
                    active={formType === 'TEACHER'}
                    iconSrc={
                      formType === 'TEACHER' ? TeacherActiveIcon : TeacherIcon
                    }
                    label="Professor"
                    onClick={() => setFormType(UserRole.TEACHER)}
                  />

                  <UserTypeIcon
                    active={formType === 'EMPLOYEE'}
                    iconSrc={
                      formType === 'EMPLOYEE'
                        ? EmployeeActiveIcon
                        : EmployeeIcon
                    }
                    label="Funcionário"
                    onClick={() => setFormType(UserRole.EMPLOYEE)}
                  />

                  <UserTypeIcon
                    active={formType === 'ADMIN'}
                    iconSrc={formType === 'ADMIN' ? AdminActiveIcon : AdminIcon}
                    label="Administrador"
                    onClick={() => setFormType(UserRole.ADMIN)}
                  />
                </>
              )}
            </div>
          </div>

          <Divider className="mx-6" />

          <div className="overflow-y-auto scroll-thin mx-1 mb-2">
            <RegisterForm mode={formMode} />
          </div>
        </div>
      </div>
    );
  };

  const renderUpdate = () => {
    return (
      <div className="w-auto h-screen flex justify-center p-5">
        <div className="hidden lg:block">
          <Button
            aria-label="Logo Plus"
            style={{
              padding: 0,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              boxShadow: 'none',
            }}
            onClick={goBack}
          >
            <img
              src={LogoPng}
              alt="Logo Plus"
              height={300}
              className={clsx('cursor-pointer')}
            />
          </Button>
        </div>
        <div
          className={clsx(
            'w-full max-w-2xl bg-white',
            'flex flex-col gap-1',
            'rounded-3xl shadow-2xl'
          )}
        >
          <div className="w-full flex flex-col items-center gap-4 p-6 relative">
            <IconButton
              color="secondary"
              className="absolute left-6 top-6"
              onClick={goBack}
            >
              <ArrowBack />
            </IconButton>
            <span className="text-lg font-bold">Alterar Dados:</span>

            <div className="flex flex-wrap gap-3 justify-center w-full">
              <UserTypeIcon
                active
                iconSrc={handleIconSrc(formType)}
                label={translateUserRoles(formType)}
              />
            </div>
          </div>

          <Divider className="mx-6" />

          <div className="overflow-y-auto scroll-thin mx-1 mb-2">
            {userToUpdate && (
              <RegisterForm mode={formMode} userToUpdate={userToUpdate} />
            )}
          </div>
        </div>
      </div>
    );
  };

  if (
    location.pathname === ROUTES.users.me ||
    location.pathname === ROUTES.users.edit(id ?? '')
  ) {
    return renderUpdate();
  }
  return renderCreate();
}
